import classNames from 'classnames';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useCreateGroupMutation,
  useAddGroupToGroupMutation,
  useGetAADWritebackGroupQuery,
  useGetACLGroupsQuery,
} from '../../graph/services/graphApi';
import CreationActions from '../../groups/components/CreationActions';
import CreationStatus from '../../groups/components/CreationStatus';
import {
  addWithRetry,
  toMailNickname,
  useGroupCreation,
  NAME_SUFFIX_ERROR,
  NAME_SUFFIX_HELP,
  NAME_SUFFIX_PATTERN,
  type CreationStep,
} from '../../groups/groupCreation';

const ACL_PREFIX = 'ACL_';

// The standard (non-confidential) ACL creation form body. The button + modal shell
// live in CreateACLGroup; this renders the name/description/writeback inputs, a live
// preview, the step status, and actions, then calls onClose on cancel or after a
// successful run finishes navigating to the new ACL.
export default function StandardACLForm({
  onClose,
  onBack,
}: {
  onClose: () => void;
  onBack?: () => void;
}) {
  const navigate = useNavigate();
  const [nameSuffix, setNameSuffix] = useState('');
  const [description, setDescription] = useState('');
  const [enableWriteback, setEnableWriteback] = useState(false);

  const { data: aclGroups } = useGetACLGroupsQuery();
  const { data: writebackGroup } = useGetAADWritebackGroupQuery();

  const [createGroup] = useCreateGroupMutation();
  const [addGroupToGroup] = useAddGroupToGroupMutation();
  const { steps, isRunning, done, failed, execute } = useGroupCreation();

  const displayName = `${ACL_PREFIX}${nameSuffix}`;

  // Validation
  const hasInvalidChars = !NAME_SUFFIX_PATTERN.test(nameSuffix);
  const nameValid = nameSuffix.trim().length > 0 && !hasInvalidChars;
  const alreadyExists = !!aclGroups?.some((g) => g.displayName === displayName);
  // Mirror ProvisionProject's gate: if writeback is requested but the AAD Group
  // Writeback group can't be resolved, block creation up front rather than create the
  // ACL group and then fail to nest it — which would leave an orphaned group.
  const writebackBlocked = enableWriteback && !writebackGroup;
  const canCreate = nameValid && !alreadyExists && !writebackBlocked;

  const handleCreate = () => {
    let aclGroupId = '';

    const creationSteps: CreationStep[] = [
      {
        errorFallback: 'Failed to create ACL group',
        label: 'Create ACL group',
        run: async () => {
          const aclGroup = await createGroup({
            // Leave description off entirely when blank rather than posting an empty string.
            description: description.trim() ? description : undefined,
            displayName,
            mailNickname: toMailNickname(displayName),
          }).unwrap();
          aclGroupId = aclGroup.id;
        },
      },
    ];

    if (enableWriteback) {
      creationSteps.push({
        errorFallback: 'Failed to add ACL to AAD Group Writeback',
        label: 'Add ACL to AAD Group Writeback',
        run: async (reporter) => {
          if (!writebackGroup) throw new Error('AAD Group Writeback group could not be resolved');
          // Forward onRetry so a replication-lag backoff shows the countdown here too,
          // not just in ProvisionProject.
          await addWithRetry(
            () => addGroupToGroup({ groupId: writebackGroup.id, memberId: aclGroupId }).unwrap(),
            reporter.onRetry
          );
        },
      });
    }

    execute(creationSteps, () => {
      onClose();
      navigate(`/acls/${aclGroupId}`);
    });
  };

  return (
    <>
      <div className="font-bold text-xl mb-4">Create standard ACL group</div>

      {!isRunning && !done && !failed && (
        <>
          {/* Name input with fixed ACL_ prefix */}
          <div className="mb-4">
            <label htmlFor="standard-acl-name" className="block font-bold mb-1 text-sm">
              Group name
            </label>
            <div className="flex items-center">
              <span className="font-mono text-sm bg-base-200 rounded-l-lg border border-r-0 border-base-300 px-3 py-2">
                {ACL_PREFIX}
              </span>
              <input
                id="standard-acl-name"
                type="text"
                className={classNames('input input-bordered w-full rounded-l-none', {
                  'input-error': hasInvalidChars,
                })}
                placeholder="SharePoint_Finance_Read"
                value={nameSuffix}
                onChange={(e) => setNameSuffix(e.target.value)}
              />
            </div>
            {hasInvalidChars ? (
              <div className="text-error text-xs mt-1">{NAME_SUFFIX_ERROR}</div>
            ) : (
              <div className="text-base-content/60 text-xs mt-1">{NAME_SUFFIX_HELP}</div>
            )}
          </div>

          {/* Description input (optional) */}
          <div className="mb-4">
            <label htmlFor="standard-acl-description" className="block font-bold mb-1 text-sm">
              Description <span className="font-normal text-base-content/60">(optional)</span>
            </label>
            <input
              id="standard-acl-description"
              type="text"
              className="input input-bordered w-full"
              placeholder="What this ACL grants access to"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* On-prem writeback toggle */}
          <div className="mb-4">
            <label className="cursor-pointer label justify-start gap-3">
              <input
                type="checkbox"
                className="toggle toggle-primary toggle-sm"
                checked={enableWriteback}
                onChange={() => setEnableWriteback(!enableWriteback)}
              />
              <span className="text-sm">Enable on-prem AD writeback for this group</span>
            </label>
          </div>

          {/* Live preview */}
          {nameValid && (
            <div className="mb-4 bg-base-200 rounded-lg p-3 text-sm">
              <div className="font-bold mb-2">Group to be created:</div>
              <div className="font-mono text-xs mb-1">{displayName}</div>
              {description.trim().length > 0 && (
                <div className="text-base-content/70 text-xs mt-2 italic">{description}</div>
              )}
              {enableWriteback && (
                <>
                  <div className="font-bold mt-3 mb-1">Membership changes:</div>
                  <div className="text-xs">AAD Group Writeback ← {displayName}</div>
                </>
              )}
            </div>
          )}

          {/* Duplicate warning */}
          {alreadyExists && (
            <div className="alert alert-error mb-4 text-sm py-2">
              An ACL group named <strong className="mx-1">{displayName}</strong> already exists.
            </div>
          )}

          {/* Writeback group warning */}
          {writebackBlocked && (
            <div className="alert alert-warning mb-4 text-sm py-2">
              AAD Group Writeback group could not be found. Disable writeback or resolve the group
              before creating.
            </div>
          )}
        </>
      )}

      {(isRunning || done || failed) && (
        <CreationStatus
          steps={steps}
          done={done}
          successMessage="ACL group created — navigating to it…"
        />
      )}

      {!done && (
        <CreationActions
          busy={isRunning}
          failed={failed}
          canConfirm={canCreate}
          confirmLabel="Create"
          onClose={onClose}
          onConfirm={handleCreate}
          onBack={onBack}
        />
      )}
    </>
  );
}
